import React, { useState } from 'react';
import { Calendar, DollarSign, MapPin, ExternalLink } from 'lucide-react';
import type { ContractResponse } from '../../lib/api/samGov';
import { cn } from '../../utils/cn';
import { ContractDetailsModal } from './ContractDetailsModal';

interface ContractTableProps {
  contracts: ContractResponse[];
}

export function ContractTable({ contracts }: ContractTableProps) {
  const [selectedContract, setSelectedContract] = useState<ContractResponse | null>(null);

  const statusColors = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-yellow-100 text-yellow-800',
    archived: 'bg-gray-100 text-gray-800',
  };

  const handleContractClick = (contract: ContractResponse) => {
    setSelectedContract(contract);
  };

  return (
    <>
      <div className="min-w-full">
        {contracts.map((contract) => (
          <div
            key={contract.id}
            onClick={() => handleContractClick(contract)}
            className="grid cursor-pointer grid-cols-12 gap-4 border-b border-gray-200 px-4 py-4 transition-colors hover:bg-gray-50 sm:px-6"
          >
            {/* Contract Details */}
            <div className="col-span-4 sm:col-span-3">
              <div className="min-w-0">
                <div className="font-medium text-gray-900 line-clamp-1">{contract.title}</div>
                <div className="mt-1 flex items-center text-sm text-gray-500">
                  <MapPin className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span className="truncate">
                    {contract.location || 'Location not specified'}
                  </span>
                </div>
                <div className="mt-1 text-sm text-gray-500">
                  NAICS: {contract.naicsCode}
                </div>
              </div>
            </div>

            {/* Agency */}
            <div className="col-span-3 hidden sm:block">
              <div className="text-sm text-gray-900">{contract.agency}</div>
              {contract.setAside && (
                <span className="mt-1 inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold text-blue-800">
                  {contract.setAside}
                </span>
              )}
            </div>

            {/* Value */}
            <div className="col-span-3 sm:col-span-2">
              <div className="flex items-center text-sm text-gray-900">
                <DollarSign className="mr-1.5 h-4 w-4 text-gray-400" />
                {contract.value
                  ? new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(contract.value)
                  : 'Not specified'}
              </div>
            </div>

            {/* Deadline */}
            <div className="col-span-3 sm:col-span-2">
              <div className="flex items-center text-sm text-gray-900">
                <Calendar className="mr-1.5 h-4 w-4 text-gray-400" />
                {new Date(contract.deadlineDate).toLocaleDateString()}
              </div>
            </div>

            {/* Status */}
            <div className="col-span-2 flex items-center justify-between">
              <span
                className={cn(
                  'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                  statusColors[contract.status]
                )}
              >
                {contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
              </span>
              <a
                href={contract.contractUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 text-gray-400 hover:text-gray-500"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-5 w-5" />
              </a>
            </div>
          </div>
        ))}
      </div>

      {selectedContract && (
        <ContractDetailsModal
          contract={selectedContract}
          isOpen={true}
          onClose={() => setSelectedContract(null)}
        />
      )}
    </>
  );
}